import { z } from 'zod';

/**
 * Padrinhos e afilhados (GS1, decidido pelo GrowuP a 1-11-2026, docs/streaming).
 *
 * - Cada streamer tem um padrinho ou madrinha (papel Padrinho/Madrinha na
 *   gestao). Cada padrinho tem um maximo de afilhados, que ajusta a sua
 *   disponibilidade.
 * - No dia em que aceita a candidatura, o Stream Staff da um padrinho
 *   **provisorio**; na reuniao mensal confirma-o ou troca-o (RN-GS4 revista).
 * - A conversa de apresentacao tem sempre alguem do Stream Staff (RN-GS11):
 *   fica o dia e quem esteve.
 */

const datetimeSchema = z.string().datetime({ offset: true });
const daySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data invalida');

/** Afilhados por padrinho quando ainda nao disse quantos quer. */
export const DEFAULT_MAX_AFILHADOS = 5;
export const MAX_AFILHADOS_LIMIT = 30;

/** Um padrinho, com a ocupacao: `free` = maximo menos afilhados (nunca negativo). */
export const padrinhoSummarySchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  maxAfilhados: z.number().int(),
  afilhados: z.number().int(),
  free: z.number().int(),
  /** Semaforo do padrinho (GS3): afilhados sem contacto e relatorios em atraso. */
  noContact: z.number().int().default(0),
  overdueReports: z.number().int().default(0),
});
export type PadrinhoSummary = z.infer<typeof padrinhoSummarySchema>;

/** O padrinho de um streamer, na lista do Stream Staff. */
export const streamerPadrinhoSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** `false` = provisorio, ate a reuniao mensal. */
  confirmed: z.boolean(),
  since: datetimeSchema,
});
export type StreamerPadrinho = z.infer<typeof streamerPadrinhoSchema>;

/** A conversa de apresentacao (RN-GS11): o dia e quem do Stream Staff esteve. */
export const introMeetingSchema = z.object({
  on: daySchema,
  by: z.string(),
});
export type IntroMeeting = z.infer<typeof introMeetingSchema>;

/**
 * `PUT /api/streaming/streamers/:id/padrinho`: atribuir, trocar, confirmar ou
 * tirar (`padrinhoId: null`). Trocar de padrinho manda email ao streamer e ao
 * padrinho novo.
 */
export const assignPadrinhoSchema = z.object({
  padrinhoId: z.string().uuid().nullable(),
  confirmed: z.boolean().default(false),
});
export type AssignPadrinho = z.input<typeof assignPadrinhoSchema>;

/** `PUT /api/streaming/padrinhos/:accountId`: o maximo de afilhados. */
export const padrinhoMaxSchema = z.object({
  maxAfilhados: z.number().int().min(0).max(MAX_AFILHADOS_LIMIT),
});
export type PadrinhoMax = z.infer<typeof padrinhoMaxSchema>;

/** O padrinho que o streamer ve no `/painel`. */
export const myPadrinhoSchema = z.object({
  login: z.string(),
  confirmed: z.boolean(),
  /** Mensagens do padrinho por ler (GS13). */
  unreadMessages: z.number().int().default(0),
});
export type MyPadrinho = z.infer<typeof myPadrinhoSchema>;

/**
 * Pedido de outro padrinho (GS1, Telmo FB-3): o streamer pede no painel, o
 * Stream Staff ve-o em Streaming > Padrinhos e trata-o ao atribuir.
 */
export const padrinhoRequestSchema = z.object({
  at: datetimeSchema,
  reason: z.string(),
});
export type PadrinhoRequest = z.infer<typeof padrinhoRequestSchema>;

const reasonSchema = z.string().trim().min(3, 'diz-nos em poucas palavras porque').max(1000);

/** `POST /api/me/padrinho-request`: o streamer pede outro padrinho. */
export const padrinhoRequestInputSchema = z.object({ reason: reasonSchema });
export type PadrinhoRequestInput = z.infer<typeof padrinhoRequestInputSchema>;

/**
 * `POST /api/streaming/padrinho/afilhados/:id/leave`: o padrinho deixa de
 * acompanhar um afilhado; o Stream Staff recebe email para atribuir outro.
 */
export const leaveAfilhadoSchema = z.object({ reason: reasonSchema });
export type LeaveAfilhado = z.infer<typeof leaveAfilhadoSchema>;

/** `POST /api/streaming/streamers/:id/intro`: a conversa de apresentacao, com quem a marca. */
export const introInputSchema = z.object({ on: daySchema });
export type IntroInput = z.infer<typeof introInputSchema>;
